import { useEffect, useRef, useState } from 'react';

import { BASE_LOGIN_CANVAS_HEIGHT, BASE_LOGIN_FRAME_WIDTH } from '../config';

type ViewportOrientation = 'portrait' | 'landscape';

export type ViewportSize = {
  width: number;
  height: number;
};

function parseOrientationLabel(
  orientationLabel: string | undefined,
): ViewportOrientation | null {
  if (!orientationLabel) {
    return null;
  }

  if (orientationLabel.startsWith('landscape')) {
    return 'landscape';
  }

  if (orientationLabel.startsWith('portrait')) {
    return 'portrait';
  }

  return null;
}

function isTextInputFocused(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLElement)) {
    return false;
  }

  if (activeElement.isContentEditable) {
    return true;
  }

  return (
    activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA'
  );
}

function resolveOrientation(): ViewportOrientation {
  if (typeof window === 'undefined') {
    return 'portrait';
  }

  const screenOrientation = parseOrientationLabel(
    window.screen?.orientation?.type,
  );
  if (screenOrientation) {
    return screenOrientation;
  }

  const legacyOrientation = (window as Window & { orientation?: number })
    .orientation;
  if (typeof legacyOrientation === 'number') {
    return Math.abs(legacyOrientation) % 180 === 90 ? 'landscape' : 'portrait';
  }

  return window.innerWidth >= window.innerHeight ? 'landscape' : 'portrait';
}

function isDesktopViewport(): boolean {
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return false;
  }

  return (
    window.matchMedia('(hover: hover)').matches &&
    window.matchMedia('(pointer: fine)').matches
  );
}

function resolveViewportAxis(
  candidates: Array<number | undefined>,
  fallbackValue: number,
): number {
  for (const candidate of candidates) {
    if (
      typeof candidate === 'number' &&
      Number.isFinite(candidate) &&
      candidate > 0
    ) {
      return Math.floor(candidate);
    }
  }

  return fallbackValue;
}

function resolveViewportSize(): ViewportSize {
  if (typeof window === 'undefined') {
    return {
      width: BASE_LOGIN_FRAME_WIDTH,
      height: BASE_LOGIN_CANVAS_HEIGHT,
    };
  }

  const documentElement = document.documentElement;

  return {
    width: resolveViewportAxis(
      [documentElement?.clientWidth, window.innerWidth],
      BASE_LOGIN_FRAME_WIDTH,
    ),
    height: resolveViewportAxis(
      [documentElement?.clientHeight, window.innerHeight],
      BASE_LOGIN_CANVAS_HEIGHT,
    ),
  };
}

export function useViewportSize() {
  const [viewportSize, setViewportSize] = useState(resolveViewportSize);
  const orientationRef = useRef(resolveOrientation());

  useEffect(() => {
    const updateViewportSize = () => {
      const nextOrientation = resolveOrientation();
      const hasOrientationChanged = nextOrientation !== orientationRef.current;
      if (hasOrientationChanged) {
        orientationRef.current = nextOrientation;
      }

      setViewportSize(currentViewportSize => {
        const nextViewportSize = resolveViewportSize();

        if (hasOrientationChanged || isDesktopViewport()) {
          return nextViewportSize;
        }

        if (isTextInputFocused()) {
          return currentViewportSize;
        }

        // Keep the mobile canvas stable when browser chrome changes its size.
        // Orientation changes and desktop window resizes still update immediately.
        return currentViewportSize;
      });
    };

    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
    window.addEventListener('orientationchange', updateViewportSize);
    window.addEventListener('focusout', updateViewportSize);

    return () => {
      window.removeEventListener('resize', updateViewportSize);
      window.removeEventListener('orientationchange', updateViewportSize);
      window.removeEventListener('focusout', updateViewportSize);
    };
  }, []);

  return viewportSize;
}
